import axios from 'axios';

// ALWAYS use relative URL - works for both preview and production
// The server handles routing /api to the backend
const API_BASE = '/api';

// CSRF Token storage (in memory, not localStorage for security)
let csrfToken = null;

export const setCsrfToken = (token) => {
  csrfToken = token;
};

export const getCsrfToken = () => csrfToken;

export const clearCsrfToken = () => {
  csrfToken = null;
};

// Create axios instance with credentials for HttpOnly cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // CRITICAL: Send cookies with every request
  timeout: 60000,  // 60 second timeout for AI chat responses
});

// Request interceptor to add CSRF token to write requests
api.interceptors.request.use(
  (config) => {
    // Add CSRF token header for write operations
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase())) {
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    const originalRequest = error.config;
    
    // If CSRF token is invalid, try to refresh it and retry the request
    if (status === 403 && detail.includes('CSRF') && !originalRequest._retry) {
      originalRequest._retry = true;
      console.warn('CSRF token invalid, attempting to refresh...');
      
      try {
        // Get a new CSRF token
        const csrfResponse = await api.get('/auth/csrf-token');
        const newToken = csrfResponse.data?.csrf_token;
        
        if (newToken) {
          setCsrfToken(newToken);
          // Retry the original request with the new token
          originalRequest.headers['X-CSRF-Token'] = newToken;
          console.log('CSRF token refreshed, retrying request...');
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Failed to refresh CSRF token:', refreshError);
        // If refresh fails, redirect to login
        clearCsrfToken();
        window.location.href = '/login';
      }
    }
    
    // If 401 error - session expired, license invalid, or not authenticated
    if (status === 401) {
      // License-specific: deactivated, deleted or expired -> redirect to renew page
      if (typeof detail === 'string' && detail.startsWith('license_invalid')) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/lizenz-erneuern') {
          console.log('License invalid, redirecting to renew page');
          window.location.href = '/lizenz-erneuern';
        }
        return Promise.reject(error);
      }
      
      const publicPaths = ['/login', '/register', '/termin', '/', '/passwort-setzen', '/passwort-vergessen', '/impressum', '/datenschutz', '/agb', '/faq', '/so-arbeiten-wir', '/ratgeber', '/leistungen', '/einsatzgebiet', '/kontakt', '/maler-dormagen'];
      const currentPath = window.location.pathname;
      const isPublicPath = publicPaths.some(path => 
        currentPath === path || currentPath.startsWith('/passwort') || currentPath.startsWith('/ratgeber') || currentPath.startsWith('/leistungen') || currentPath.startsWith('/maler-')
      );
      
      if (!isPublicPath) {
        console.log('Session expired, redirecting to login');
        clearCsrfToken();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  getCsrfToken: () => api.get('/auth/csrf-token'),
  changePassword: (currentPassword, newPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
};

// Appointments API
export const appointmentsAPI = {
  getSlots: (startDate, endDate) => 
    api.get(`/appointments/slots?start_date=${startDate}&end_date=${endDate}`),
  create: (appointmentData) => api.post('/appointments', appointmentData),
  createGuest: (bookingData) => api.post('/appointments/guest', bookingData),
  getAll: () => api.get('/appointments'),
  getById: (id) => api.get(`/appointments/${id}`),
  update: (id, updateData) => api.patch(`/appointments/${id}`, updateData),
  cancel: (id) => api.delete(`/appointments/${id}`),
  blockDate: (date) => api.post('/appointments/block-date', { date }),
  unblockDate: (date) => api.delete(`/appointments/block-date/${date}`),
  getBlockedDates: () => api.get('/appointments/blocked-dates'),
  getTodayAppointments: () => api.get('/admin/appointments/today'),
  // Admin functions
  adminCreate: (data) => api.post('/admin/appointments', data),
  adminUpdate: (id, data) => api.put(`/admin/appointments/${id}`, data),
  adminDelete: (id) => api.delete(`/admin/appointments/${id}`),
};

// Customers API
export const customersAPI = {
  getAll: (search = '') => api.get(`/customers${search ? `?search=${search}` : ''}`),
  getById: (id) => api.get(`/customers/${id}`),
  create: (customerData) => api.post('/customers', customerData),
  update: (id, updateData) => api.patch(`/customers/${id}`, updateData),
};

// Payments API
export const paymentsAPI = {
  getAll: () => api.get('/payments'),
  getByCustomer: (customerId) => api.get(`/payments/${customerId}`),
  create: (paymentData) => api.post('/payments', paymentData),
};

// Balance API
export const balanceAPI = {
  get: () => api.get('/balance'),
  getByCustomer: (customerId) => api.get(`/balance/${customerId}`),
  update: (customerId, amount, description) => 
    api.post(`/balance/${customerId}`, { amount, description }),
  getHistory: () => api.get('/balance-history'),
  getHistoryByCustomer: (customerId) => api.get(`/balance-history/${customerId}`),
};

// Files API
export const filesAPI = {
  getAll: () => api.get('/files'),
  getByCustomer: (customerId) => api.get(`/files/${customerId}`),
  upload: (customerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/files/${customerId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => api.get(`/files/download/${fileId}`, { responseType: 'blob' }),
  delete: (fileId) => api.delete(`/files/${fileId}`),
};

// Email API
export const emailAPI = {
  send: (recipientEmail, subject, htmlContent) => 
    api.post('/email/send', { recipient_email: recipientEmail, subject, html_content: htmlContent }),
  sendCustomerNumber: (customerId, templateType, customSubject = null, customContent = null) => 
    api.post('/email/send-customer-number', { 
      customer_id: customerId, 
      template_type: templateType,
      custom_subject: customSubject,
      custom_content: customContent,
    }),
};

// Admin Dashboard API
export const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard'),
};

// Holidays API
export const holidaysAPI = {
  getByYear: (year) => api.get(`/holidays/${year}`),
};

export default api;
